import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Target, Shield, Sunset, Calculator, Globe, ArrowRight } from 'lucide-react';
import { services } from '../data/mockData';

const iconMap = {
  TrendingUp,
  Target,
  Shield,
  Sunset,
  Calculator,
  Globe
};

const ServicesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef} 
      className="py-28 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #f1f3f5 100%)'
      }}
    >
      {/* Subtle Pattern */}
      <div className="absolute inset-0 opacity-40" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.02) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between mb-16 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div>
            <span className="text-gray-400 text-sm uppercase tracking-widest mb-4 block">OUR SERVICES</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              How Frontier can
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600">
                help you
              </span>
            </h2>
          </div>
          <Link
            to="/services"
            className="mt-6 lg:mt-0 inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors duration-300 group"
          >
            View all services
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Link
                key={service.id}
                to={service.link}
                className={`group relative p-8 rounded-3xl border border-gray-200 bg-white shadow-sm cursor-pointer transform transition-all duration-700 hover:scale-[1.02] hover:shadow-xl hover:border-gray-300 ${
                  isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onMouseEnter={() => setHoveredCard(service.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="relative z-10">
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.color} mb-6 transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                    {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-all duration-300">
                    {service.title}
                  </h3>

                  <p className="text-gray-500 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="flex items-center gap-2 text-gray-400 group-hover:text-gray-700 transition-colors duration-300">
                    <span className="text-sm font-medium">Learn more</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className={`mt-20 text-center transform transition-all duration-1000 delay-500 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <Link
            to="/portfolio-upload"
            data-testid="services-review-portfolio-btn"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 hover:shadow-xl transition-all duration-500 group"
          >
            <span>Review my portfolio</span>
            <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <p className="text-gray-400 text-sm mt-4">Book a consultation with our experts</p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
